import React, {useEffect} from "react";
import Resources from "../../../../data/services/resources";
import {getDialogInfoResource} from "../../../../data/actions/dialogInfoResource";
import {Field} from "../../../../data/services/fields";
import LocalStorage from "../../../../common/util/localStorage";
import {classNames, getProp} from "../../../../common/util/util-helpers";
import {toFrontDateTime} from "../../../../common/util/util-dates";
import {LoaderLarge} from "../../../../common/components/loader";
import DataCard from "../../../../common/components/display-data/data-card";
import Image from "../../../../common/components/image";
import {RESULT_BADGE_CLASS} from "../dvir-template-utils";

export default function SubmissionInfoCard({DvirSubmissionID, translate, dispatch, dialogInfoResource, onPhotoClick}) {
    function getHeaderFields() {
        return {
            driver_name: new Field('driver_name', '', [''], false, "text", {label: 'Driver'}),
            truck_number: new Field('truck_number', '', [''], false, "text", {label: 'Truck'}),
            trailer_number: new Field('trailer_number', '', [''], false, "text", {label: 'Trailer'}),
            submitted_at: new Field('submitted_at', '', [''], false, "custom", {
                label: 'SubmittedAt',
                render: (it) => toFrontDateTime(it.submitted_at)
            }),
            odometer: new Field('odometer', '', [''], false, "text", {label: 'Odometer'}),
            engine_hours: new Field('engine_hours', '', [''], false, "text", {label: 'EngineHours'}),
            latitude: new Field('latitude', '', [''], false, "text", {label: 'Latitude'}),
            longitude: new Field('longitude', '', [''], false, "text", {label: 'Longitude'})
        };
    }

    const data = getProp(dialogInfoResource, "data", {});
    const isLoading = getProp(dialogInfoResource, "isLoading", true);

    // DataCard only skips null/undefined/""/" " values, so a legitimate 0 (e.g. odometer)
    // must still pass through here to keep the border-between-rows arithmetic below correct.
    const headerFields = Object.values(getHeaderFields()).filter(it => {
        const value = data[it.name];

        return value !== null && value !== undefined && value !== '';
    });
    const answers = getProp(data, "answers", []).slice().sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));
    const resultKey = ['passed', 'defects', 'na'].includes(data.result) ? data.result : 'na';

    useEffect(() => {
        dispatch(getDialogInfoResource({
            user: LocalStorage.get("user"),
            resource: Resources.DvirSubmissionsInfo,
            query: {id: DvirSubmissionID}
        }));
    }, [DvirSubmissionID]);

    if (isLoading) {
        return <div className="p-6"><LoaderLarge/></div>;
    }

    return (
        <div className="p-6 space-y-6">
            <div>
                <div className="flex items-center gap-3 mb-3">
                    <p className="text-2xl font-bold text-tm-gray-900">{data.template_name}</p>

                    <span className={classNames(
                        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                        RESULT_BADGE_CLASS[resultKey]
                    )}>
                        {translate('text.dvir_result_' + resultKey)}
                    </span>
                </div>

                <div className="bg-inverse rounded-xl border-tm-gray-300 border shadow-card">
                    {headerFields.map((field, i) => (
                        <DataCard
                            key={field.name}
                            displayField={field}
                            fieldsData={data}
                            className={classNames(
                                "px-6 py-1.5 flex items-center",
                                i < headerFields.length - 1 ? "border-b border-tm-gray-300" : undefined
                            )}
                            translate={translate}
                        />
                    ))}
                </div>
            </div>

            <div className="space-y-3">
                {answers.map((answer) => {
                    const isDefect = !!(answer.triggered?.create_defect || answer.triggered?.block_dispatch);

                    return (
                        <div
                            key={answer.id}
                            className={classNames(
                                "bg-inverse rounded-xl border shadow-card p-4",
                                isDefect ? "border-red-400 border-l-4" : "border-tm-gray-300"
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-semibold text-tm-gray-900">
                                        {answer.step_order}. {answer.question_name}
                                    </p>

                                    {!!answer.selected_option_label && (
                                        <p className="text-tm-gray-700 mt-1">{answer.selected_option_label}</p>
                                    )}

                                    {!!answer.note && (
                                        <p className="text-tm-gray-600 text-sm mt-1 italic">{answer.note}</p>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-1 shrink-0">
                                    {isDefect && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                            {translate('text.dvir_defect')}
                                        </span>
                                    )}

                                    {!!answer.triggered?.warn_safety && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                            {translate('text.dvir_action_safety')}
                                        </span>
                                    )}

                                    {!!answer.triggered?.warn_dispatch && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                            {translate('text.dvir_action_dispatch')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {!!(answer.photos ?? []).length && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {answer.photos.map((photo) => (
                                        <button
                                            key={photo.id}
                                            type="button"
                                            onClick={() => onPhotoClick(photo.id)}
                                            className="rounded-lg overflow-hidden border border-tm-gray-300 hover:border-primary-600"
                                        >
                                            <Image
                                                resourcePath={Resources.DvirSubmissionsPhoto}
                                                params={{id: photo.id}}
                                                className="h-24 w-24 object-cover"
                                                alt={answer.question_name}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
